/**
 * Free Score Hero — PDF-first, consent-first entry point.
 *
 * 2026-06 redesign to match the approved mockup:
 *   LEFT  — eyebrow, "open doors" headline, description, avatar strip,
 *           benefit pills, example-audit card (Senior B2B leader, 76).
 *   RIGHT — white glowing card: FREE AUDIT badge, pickers, dropzone that
 *           STAGES the PDF, single ownership-consent checkbox, gradient
 *           "Analyse my LinkedIn →" CTA, secure-processing footer.
 */
import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  ArrowRight,
  CheckCircle2,
  FileText,
  Loader2,
  Lock,
  PenLine,
  Search,
  ShieldCheck,
  Sparkles,
  Upload,
  Zap,
} from 'lucide-react';
import { fadeUp } from './shared';
import { fetchServerCurrency, detectCurrency, billingApi } from '../../lib/billing';
import { useAuth } from '../../lib/auth-context';
import { setPendingFreeAuditPdf } from '../../lib/pendingFreeAuditPdf';
import { LinkedInExportGuideDark } from './LinkedInExportGuideDark';
import { trackLead } from '../../lib/analytics';

const CONSENT_TEXT_ANALYSIS = (
  'I confirm that this profile belongs to me or that I am authorised by the profile owner to submit it. ' +
  'I consent to Blayzrr analysing and temporarily storing the information I provide to generate my profile score, recommendations and rewrites.'
);
const CONSENT_TEXT_TERMS = 'By continuing you agree to the Terms & Conditions & Privacy Policy.';
const CONSENT_VERSION = '2.1';

function getHeroClientId() {
  try {
    let id = localStorage.getItem('blayzrr_consent_client_id');
    if (!id) {
      id = crypto?.randomUUID ? crypto.randomUUID() : `cid-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      localStorage.setItem('blayzrr_consent_client_id', id);
    }
    return id;
  } catch { return `cid-${Date.now()}`; }
}

async function logHeroConsent(purpose, accepted, text) {
  try {
    await axios.post(`${process.env.REACT_APP_BACKEND_URL || ''}/api/consent/log`, {
      choice: accepted ? 'accepted' : 'rejected',
      client_id: getHeroClientId(),
      page_url: typeof window !== 'undefined' ? window.location.href : '',
      consent_version: CONSENT_VERSION,
      purpose,
      consent_text: text,
    }, { timeout: 6000 });
  } catch { /* fire and forget */ }
}

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

// Avatar strip — four small overlapping portraits reading as "people
// like you" (founders, professionals, consultants).
const AVATARS = [
  'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=96&h=96&q=80',
  'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=96&h=96&q=80',
  'https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=96&h=96&q=80',
  'https://images.unsplash.com/photo-1531384441138-2736e62e0919?auto=format&fit=crop&w=96&h=96&q=80',
];
// Example-audit card portrait — senior B2B leader.
const EXAMPLE_PORTRAIT = 'https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=160&h=160&q=80';

const BENEFIT_PILLS = [
  { icon: Search, label: 'Improve visibility' },
  { icon: ShieldCheck, label: 'Build credibility' },
  { icon: PenLine, label: 'Clarify positioning' },
];

const EXAMPLE_METRICS = [
  { label: 'Positioning', pct: 82 },
  { label: 'Credibility', pct: 74 },
  { label: 'Discoverability', pct: 58 },
];

const STATUSES = [
  { key: 'professional', label: 'Working professional' },
  { key: 'student_fresher', label: 'Student / Fresher' },
  { key: 'founder_business_owner', label: 'Founder / Owner' },
  { key: 'consultant_freelancer', label: 'Consultant / Freelancer' },
];

const OBJECTIVES = {
  professional: [
    { key: 'recruiter_visibility', label: 'Recruiter visibility' },
    { key: 'thought_leadership', label: 'Thought leadership' },
    { key: 'business_development', label: 'Business development' },
  ],
  student_fresher: [
    { key: 'jobs_internships', label: 'Jobs & internships' },
    { key: 'learning_thought_leadership', label: 'Learning-led TL' },
    { key: 'projects_networking', label: 'Projects & networking' },
  ],
  founder_business_owner: [
    { key: 'business_development', label: 'Business development' },
    { key: 'thought_leadership', label: 'Thought leadership' },
    { key: 'recruiter_visibility', label: 'Hiring & credibility' },
  ],
  consultant_freelancer: [
    { key: 'business_development', label: 'Client discovery' },
    { key: 'thought_leadership', label: 'Thought leadership' },
    { key: 'recruiter_visibility', label: 'Corporate visibility' },
  ],
};

export function FreeScoreHero() {
  const [status, setStatus] = useState('');
  const [objective, setObjective] = useState('');
  const [consentAnalysis, setConsentAnalysis] = useState(false);
  const [contextTouched, setContextTouched] = useState(false);
  const [stagedFile, setStagedFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [result, setResult] = useState(null);
  const [unlocking, setUnlocking] = useState(false);
  const [, setCurrency] = useState(detectCurrency());
  const [country, setCountry] = useState('');
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  React.useEffect(() => {
    let live = true;
    fetchServerCurrency().then((geo) => {
      if (!live || !geo) return;
      setCountry(geo.country || '');
      if (geo.currency) setCurrency(geo.currency);
    });
    return () => { live = false; };
  }, []);

  const objectivesForStatus = OBJECTIVES[status] || [];
  const isNonIndia = country && country !== 'IN';
  // Post-score funnel (2026-06): logged-out + logged-in-unpaid users go to
  // Pricing; logged-in PAID users go straight to the Dashboard where the
  // same PDF is pre-loaded (see pendingFreeAuditPdf handoff).
  const planPricingHref = isNonIndia ? '/pricing?currency=USD&plan=one_time' : '/pricing?plan=one_time';

  const onUnlock = useCallback(async () => {
    if (!user) {
      navigate(planPricingHref);
      return;
    }
    setUnlocking(true);
    try {
      const me = await billingApi.me();
      navigate(me?.status === 'active' ? '/dashboard' : planPricingHref);
    } catch {
      navigate(planPricingHref);
    } finally {
      setUnlocking(false);
    }
  }, [user, navigate, planPricingHref]);

  const stageFile = useCallback((file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please upload your LinkedIn PDF (Profile \u2192 Resources \u2192 Save to PDF).');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('PDF too large (max 10MB).');
      return;
    }
    setStagedFile(file);
    setResult(null);
  }, []);

  const analyse = useCallback(async () => {
    if (busy) return;
    if (!stagedFile) {
      toast.error('Drop your LinkedIn PDF in the box first.');
      return;
    }
    if (!contextTouched || !status || !objective) {
      toast.error('Pick "You are" + Your goal above so we can score you against the right benchmark.');
      return;
    }
    if (!consentAnalysis) {
      toast.error('Please confirm the checkbox before analysing.');
      return;
    }
    setBusy(true);
    setResult(null);
    void logHeroConsent('terms_privacy', true, CONSENT_TEXT_TERMS);
    const t = toast.loading('Reading your PDF \u2026');
    try {
      const clientId = typeof localStorage !== 'undefined' ? (localStorage.getItem('blayzrr_consent_client_id') || '') : '';
      const fd = new FormData();
      fd.append('file', stagedFile);
      const { data } = await axios.post(
        `${BACKEND}/api/free-score/from-pdf?status=${encodeURIComponent(status)}&objective=${encodeURIComponent(objective)}&consent_client_id=${encodeURIComponent(clientId)}`,
        fd,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 90000,
        },
      );
      setResult(data);
      // GA4: free-score hero converts as a lead (PDF was uploaded + scored).
      trackLead('free_score_hero', { status, objective });
      // Keep the SAME file around so the Dashboard can pre-load it for
      // the full (paid) audit without a re-upload.
      setPendingFreeAuditPdf(stagedFile);
      toast.success('Score ready \u2014 unlock your full audit \u2192', { id: t });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = (typeof detail === 'object' && detail?.message) || detail || err?.message || 'Could not score that PDF.';
      toast.error(msg, { id: t });
    } finally {
      setBusy(false);
    }
  }, [busy, stagedFile, consentAnalysis, contextTouched, status, objective]);

  const onFilePick = (e) => {
    const f = e.target.files?.[0];
    stageFile(f);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    stageFile(e.dataTransfer.files?.[0]);
  };

  const canAnalyse = !!stagedFile && consentAnalysis && !!status && !!objective && !busy;

  return (
    <section
      className="relative overflow-x-clip isolate bg-[#050b1a] text-slate-100"
      aria-label="Free LinkedIn profile score"
      id="ads-hero-calculator"
      data-testid="free-score-hero"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(900px circle at 78% 40%, hsl(190 95% 55% / 0.16), transparent 60%), radial-gradient(600px circle at 8% 8%, hsl(212 92% 55% / 0.10), transparent 60%)',
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(148,197,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(148,197,255,0.5) 1px, transparent 1px)',
          backgroundSize: '44px 44px',
        }}
      />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-10 py-12 sm:py-14 lg:py-16 xl:py-20">
        <div className="grid gap-12 lg:grid-cols-[minmax(0,1.02fr)_minmax(0,1fr)] lg:gap-12 xl:gap-16 items-start">
          {/* LEFT */}
          <motion.div {...fadeUp} className="relative lg:sticky lg:top-24 lg:self-start">
            <p
              className="font-mono-edge text-[11px] sm:text-[12px] tracking-[0.3em] uppercase text-cyan-300"
              data-testid="free-score-hero-eyebrow"
            >
              LinkedIn Presence Intelligence
            </p>

            <h1
              className="mt-4 font-display font-semibold tracking-[-0.04em] leading-[1.02] text-white text-[38px] sm:text-[48px] lg:text-[54px] xl:text-[62px]"
              data-testid="free-score-hero-headline"
            >
              Your LinkedIn should{' '}
              <span className="text-cyan-400">open doors.</span>
              <br />
              See what&rsquo;s holding it back.
            </h1>

            <p
              className="mt-5 text-[15px] sm:text-base text-slate-300/90 leading-relaxed max-w-[560px]"
              data-testid="free-score-hero-subline"
            >
              see how your positioning, discoverability, credibility and proof
              are performing&mdash;then get prioritized actions to strengthen
              your presence and create more career and business opportunities.
            </p>

            <div className="mt-6 flex items-center gap-4" data-testid="free-score-hero-social-proof">
              <div className="flex -space-x-3">
                {AVATARS.map((src, i) => (
                  <img
                    key={src}
                    src={src}
                    alt=""
                    loading="eager"
                    decoding="async"
                    className="h-11 w-11 sm:h-12 sm:w-12 rounded-full object-cover ring-2 ring-[#050b1a]"
                    style={{ zIndex: AVATARS.length - i }}
                  />
                ))}
              </div>
              <p className="text-[13px] text-slate-300 leading-snug max-w-[240px]">
                Built for founders, professionals &amp; independent consultants
              </p>
            </div>

            <div className="mt-5 flex flex-wrap gap-2.5" data-testid="free-score-hero-benefit-pills">
              {BENEFIT_PILLS.map(({ icon: Icon, label }) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-4 h-9 text-[12.5px] text-slate-100"
                  data-testid={`free-score-hero-pill-${label.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <Icon size={13} strokeWidth={2.2} className="text-cyan-300" aria-hidden="true" />
                  {label}
                </span>
              ))}
            </div>

            {/* Example audit card */}
            <div
              className="mt-5 rounded-2xl border border-white/10 bg-[#0a1330]/85 backdrop-blur-sm p-4 sm:p-5 max-w-[560px]"
              data-testid="free-score-hero-example-card"
            >
              <div className="flex items-center gap-4 sm:gap-5">
                <div className="relative shrink-0">
                  <img
                    src={EXAMPLE_PORTRAIT}
                    alt="Example LinkedIn audit subject"
                    loading="eager"
                    decoding="async"
                    className="h-16 w-16 sm:h-[72px] sm:w-[72px] rounded-full object-cover ring-2 ring-white/15"
                  />
                  <span className="absolute -bottom-1.5 -left-1.5 inline-flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-cyan-400 to-sky-500 text-[13px] font-semibold text-slate-900 ring-2 ring-[#0a1330]">
                    76
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-mono-edge text-[10px] tracking-[0.24em] uppercase text-cyan-300">
                    Example LinkedIn Audit
                  </p>
                  <p className="mt-0.5 text-[14px] font-semibold text-white">Senior B2B leader</p>
                  <div className="mt-2 space-y-1.5">
                    {EXAMPLE_METRICS.map((m) => (
                      <div key={m.label} className="flex items-center gap-3">
                        <span className="w-[104px] shrink-0 text-[11.5px] text-slate-300">{m.label}</span>
                        <span className="h-1.5 flex-1 rounded-full bg-white/10 overflow-hidden">
                          <span
                            className="block h-full rounded-full bg-gradient-to-r from-cyan-400 to-sky-500"
                            style={{ width: `${m.pct}%` }}
                          />
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* RIGHT — white score card */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.08 }}
            className="relative w-full max-w-[460px] lg:justify-self-end"
          >
            <div
              className="relative rounded-3xl border border-cyan-300/60 bg-white p-5 sm:p-6"
              data-testid="free-score-hero-card"
              style={{
                boxShadow:
                  '0 0 0 1px rgba(34,211,238,0.25) inset,' +
                  ' 0 0 40px 4px rgba(34,211,238,0.30),' +
                  ' 0 0 120px 12px rgba(34,211,238,0.22),' +
                  ' 0 40px 140px -30px rgba(34,211,238,0.5)',
              }}
            >
              <div className="flex justify-center">
                <div
                  className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/50 bg-emerald-400/10 px-3.5 h-7 text-[10.5px] font-mono-edge tracking-[0.16em] uppercase text-emerald-700"
                  data-testid="free-score-hero-card-eyebrow"
                >
                  <Sparkles size={11} aria-hidden="true" />
                  Free Audit &middot; No card required
                </div>
              </div>

              <h2 className="mt-3 text-center font-display font-semibold text-slate-900 text-[22px] sm:text-[24px] leading-tight tracking-[-0.02em]">
                Score your LinkedIn in 10 seconds
              </h2>

              <div className="mt-2 flex items-start justify-between gap-3">
                <p className="text-[13px] text-slate-600 flex-1">
                  Export your LinkedIn as PDF,
                  <br className="hidden sm:block" /> then drop it here.
                </p>
                <LinkedInExportGuideDark />
              </div>

              <div className="mt-4">
                {!contextTouched && (
                  <p
                    className="text-[12px] font-medium text-sky-700 mb-2 flex items-center gap-1.5"
                    data-testid="free-score-hero-context-prompt"
                  >
                    <span aria-hidden="true">&#128073;</span> Confirm your status and goal so we score you against the right benchmark.
                  </p>
                )}
                <div className="grid grid-cols-2 gap-2.5">
                <div>
                  <p className="text-[10px] font-mono-edge tracking-[0.22em] uppercase text-slate-500 mb-1">You are</p>
                  <select
                    value={status}
                    onChange={(e) => {
                      const s = e.target.value;
                      setStatus(s);
                      // Reset objective when status changes so user must
                      // explicitly pick a goal (prevents stale selections).
                      setObjective('');
                      setContextTouched(true);
                    }}
                    className={`w-full h-10 rounded-lg bg-white border text-slate-900 text-[13px] px-2.5 transition-all focus:border-sky-500 focus:outline-none ${
                      contextTouched && status ? 'border-slate-300' : 'border-sky-500 attention-glow'
                    }`}
                    data-testid="free-score-hero-status"
                  >
                    <option value="" disabled>Select…</option>
                    {STATUSES.map((s) => (
                      <option key={s.key} value={s.key}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <p className="text-[10px] font-mono-edge tracking-[0.22em] uppercase text-slate-500 mb-1">Your goal</p>
                  <select
                    value={objective}
                    onChange={(e) => { setObjective(e.target.value); setContextTouched(true); }}
                    disabled={!status}
                    className={`w-full h-10 rounded-lg bg-white border text-slate-900 text-[13px] px-2.5 transition-all focus:border-sky-500 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed ${
                      contextTouched && objective ? 'border-slate-300' : 'border-sky-500 attention-glow'
                    }`}
                    data-testid="free-score-hero-objective"
                  >
                    <option value="" disabled>{status ? 'Select…' : 'Pick "You are" first'}</option>
                    {objectivesForStatus.map((o) => (
                      <option key={o.key} value={o.key}>{o.label}</option>
                    ))}
                  </select>
                </div>
                </div>
              </div>

              {/* Dropzone — stages the PDF; analysis runs on CTA click */}
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,.pdf"
                onChange={onFilePick}
                className="hidden"
                data-testid="free-score-hero-file-input"
              />
              <div
                onClick={() => !busy && fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                className={`mt-4 group rounded-2xl border-2 border-dashed transition-all px-4 py-6 text-center ${
                  dragOver
                    ? 'border-sky-500 bg-sky-50'
                    : stagedFile
                      ? 'border-emerald-400 bg-emerald-50/60'
                      : 'border-slate-300 bg-slate-50 hover:border-sky-400'
                } ${busy ? 'opacity-60 cursor-progress' : 'cursor-pointer'}`}
                data-testid="free-score-hero-dropzone"
              >
                <div className={`inline-flex h-11 w-11 items-center justify-center rounded-full mb-2 ${
                  stagedFile ? 'bg-emerald-500/15 text-emerald-600' : 'bg-sky-500/10 text-sky-500'
                }`}
                >
                  {busy
                    ? <Loader2 size={19} className="animate-spin" />
                    : stagedFile
                      ? <FileText size={19} />
                      : <Upload size={19} />}
                </div>
                {stagedFile ? (
                  <>
                    <p className="text-[13.5px] font-semibold text-slate-900 break-all" data-testid="free-score-hero-staged-filename">
                      {stagedFile.name}
                    </p>
                    <p className="text-[11.5px] text-slate-500 mt-0.5">
                      {(stagedFile.size / 1024 / 1024).toFixed(1)} MB &middot; click to replace
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-[14px] font-semibold text-slate-900">Drop your LinkedIn PDF here</p>
                    <p className="text-[12px] text-slate-500 mt-0.5">or click to browse &middot; PDF up to 10 MB</p>
                  </>
                )}
              </div>

              {/* Single ownership-consent checkbox */}
              <label
                className={`mt-4 flex items-start gap-2.5 rounded-xl border px-3 py-2.5 text-[12px] leading-snug text-slate-800 cursor-pointer transition-all ${
                  consentAnalysis ? 'border-slate-200 bg-slate-50' : 'border-sky-500 attention-glow bg-white'
                }`}
                data-testid="free-score-hero-consent-analysis-row"
              >
                <input
                  type="checkbox"
                  checked={consentAnalysis}
                  onChange={() => {
                    const next = !consentAnalysis;
                    setConsentAnalysis(next);
                    if (next) void logHeroConsent('profile_analysis', true, CONSENT_TEXT_ANALYSIS);
                  }}
                  className="mt-0.5 h-4 w-4 shrink-0 rounded accent-sky-600"
                  data-testid="free-score-hero-consent-analysis-checkbox"
                  aria-required="true"
                />
                <span>
                  I confirm that this profile belongs to me or that I am
                  authorised by the profile owner to submit it.
                </span>
              </label>

              <button
                type="button"
                onClick={analyse}
                disabled={!canAnalyse}
                className={`mt-4 w-full h-12 inline-flex items-center justify-center gap-2 rounded-xl text-[15px] font-semibold transition-all ${
                  canAnalyse
                    ? 'bg-gradient-to-r from-emerald-400 via-cyan-400 to-sky-500 text-slate-900 hover:brightness-105 shadow-[0_10px_30px_-10px_rgba(34,211,238,0.6)]'
                    : 'bg-gradient-to-r from-emerald-400/50 via-cyan-400/50 to-sky-500/50 text-slate-700/70 cursor-not-allowed'
                }`}
                data-testid="free-score-hero-submit"
              >
                {busy ? <Loader2 size={17} className="animate-spin" /> : null}
                {busy ? 'Analysing\u2026' : 'Analyse my LinkedIn'}
                {!busy && <ArrowRight size={16} aria-hidden="true" />}
              </button>

              <p className="mt-2 text-center text-[11px] text-slate-500" data-testid="free-score-hero-terms-line">
                By continuing you agree to our{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-sky-700 underline underline-offset-2 hover:text-sky-800">
                  Terms &amp; Conditions
                </a>{' '}
                &amp;{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-sky-700 underline underline-offset-2 hover:text-sky-800">
                  Privacy Policy
                </a>
                .
              </p>

              <p className="mt-3 flex items-center justify-center gap-1.5 text-[11.5px] text-slate-500" data-testid="free-score-hero-reassurance">
                <Lock size={11} className="text-slate-400" aria-hidden="true" />
                No scraping &middot; No password &middot; Your file is processed securely
              </p>

              {result && (
                <div
                  role="status"
                  aria-live="polite"
                  className="mt-6 rounded-2xl border border-sky-500/30 bg-[#0a1330]/90 p-5"
                  data-testid="free-score-hero-result"
                >
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-sky-500/40 bg-sky-500/10 px-2.5 py-1 text-[10.5px] font-mono-edge tracking-[0.18em] uppercase text-sky-200">
                      <ShieldCheck size={11} /> {result.confidence} confidence
                    </span>
                    <span className="text-[10.5px] font-mono-edge tracking-[0.18em] uppercase text-slate-400">
                      Framework v{result.scoring_version?.split('-').pop() || '1.0'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <ResultTile label="Profile readiness" value={result.score} tone="accent" />
                    <ResultTile label="Data completeness" value={Math.round((result.data_completeness || 0) * 100)} tone="muted" unit="%" />
                  </div>

                  {/* Evidence panel — proves we read the WHOLE headline + summary,
                      addressing the "you only captured 4-5 words" complaint. */}
                  {result.extracted && (result.extracted.headline_text || result.extracted.about_excerpt) && (
                    <div
                      className="mt-4 rounded-xl border border-sky-500/20 bg-sky-500/[0.04] p-3.5"
                      data-testid="free-score-hero-extracted"
                    >
                      <p className="text-[10.5px] font-mono-edge tracking-[0.22em] uppercase text-sky-300">
                        What we read from your PDF
                      </p>
                      {result.extracted.headline_text && (
                        <div className="mt-2">
                          <p className="text-[10.5px] uppercase tracking-[0.16em] text-slate-500 mb-0.5">
                            Headline · {result.extracted.headline_len} chars
                          </p>
                          <p
                            className="text-[12.5px] leading-snug text-slate-200 break-words"
                            data-testid="free-score-hero-extracted-headline"
                          >
                            {result.extracted.headline_text}
                          </p>
                        </div>
                      )}
                      {result.extracted.about_excerpt && (
                        <div className="mt-2.5">
                          <p className="text-[10.5px] uppercase tracking-[0.16em] text-slate-500 mb-0.5">
                            Summary · {result.extracted.about_len} chars
                          </p>
                          <p className="text-[12.5px] leading-snug text-slate-300 break-words">
                            {result.extracted.about_excerpt}
                            {result.extracted.about_len > result.extracted.about_excerpt.length && '…'}
                          </p>
                        </div>
                      )}
                      {Array.isArray(result.extracted.top_skills) && result.extracted.top_skills.length > 0 && (
                        <div className="mt-2.5">
                          <p className="text-[10.5px] uppercase tracking-[0.16em] text-slate-500 mb-1">Top skills</p>
                          <div className="flex flex-wrap gap-1.5">
                            {result.extracted.top_skills.map((s, i) => (
                              <span key={i} className="rounded-full border border-white/10 bg-white/[0.04] px-2 py-0.5 text-[11px] text-slate-200">{s}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {Array.isArray(result.strengths) && result.strengths.length > 0 && (
                    <div
                      className="mt-4 rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] p-3.5"
                      data-testid="free-score-hero-strengths"
                    >
                      <p className="text-[10.5px] font-mono-edge tracking-[0.22em] uppercase text-emerald-300">
                        What&rsquo;s already working
                      </p>
                      <ul className="mt-2 space-y-1.5">
                        {result.strengths.slice(0, 3).map((s, i) => (
                          <li key={i} className="flex items-start gap-2 text-[12.5px] leading-snug text-slate-200">
                            <CheckCircle2 size={13} className="mt-0.5 shrink-0 text-emerald-300" />
                            <span>{s}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {Array.isArray(result.gaps) && result.gaps.length > 0 && (
                    <div
                      className="mt-3 rounded-xl border border-amber-500/25 bg-amber-500/[0.04] p-3.5"
                      data-testid="free-score-hero-gaps"
                    >
                      <p className="text-[10.5px] font-mono-edge tracking-[0.22em] uppercase text-amber-300">
                        What&rsquo;s holding your score back
                      </p>
                      <ul className="mt-2 space-y-1.5">
                        {result.gaps.slice(0, 3).map((g, i) => (
                          <li key={i} className="flex items-start gap-2 text-[12.5px] leading-snug text-slate-200">
                            <span className="mt-1 shrink-0 h-1.5 w-1.5 rounded-full bg-amber-400" />
                            <span>{g}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {result.limiting_factor && (
                    <p className="mt-4 text-[12.5px] text-slate-300/90 leading-relaxed">
                      <span className="text-sky-300 font-semibold">Biggest blocker:</span> {result.limiting_factor}
                    </p>
                  )}

                  <GapBlock label="Headline gap" gap={result.headline_gap} testid="free-score-hero-headline-gap" />
                  <GapBlock label="About-section gap" gap={result.about_gap} testid="free-score-hero-about-gap" />

                  {result.headline_preview && (
                    <div className="mt-3 rounded-xl border border-sky-500/20 bg-white/[0.02] p-3">
                      <p className="text-[10.5px] font-mono-edge tracking-[0.22em] uppercase text-slate-400">Improved headline preview</p>
                      <p className="mt-1 text-[13px] text-slate-100 italic leading-snug">&ldquo;{result.headline_preview}&rdquo;</p>
                    </div>
                  )}

                  {/* Unlock recommendations — two conversion hooks the paid
                      audit promises to deliver. Sits directly above the CTA
                      so the click feels earned. */}
                  {Array.isArray(result.unlock_recommendations) && result.unlock_recommendations.length > 0 && (
                    <div
                      className="mt-4 rounded-xl border border-sky-500/40 bg-gradient-to-br from-sky-500/[0.09] via-sky-500/[0.04] to-transparent p-3.5"
                      data-testid="free-score-hero-unlock-recs"
                    >
                      <p className="flex items-center gap-1.5 text-[10.5px] font-mono-edge tracking-[0.22em] uppercase text-sky-300">
                        <Zap size={12} /> What your full audit unlocks
                      </p>
                      <p className="mt-1 text-[11.5px] text-slate-400 leading-snug">
                        The free audit above is a fraction of what the full audit ships. Here&rsquo;s the rest.
                      </p>
                      <ul className="mt-2.5 space-y-2">
                        {result.unlock_recommendations.map((rec, i) => (
                          <li
                            key={i}
                            className="rounded-lg border border-white/5 bg-white/[0.03] p-2.5"
                            data-testid={`free-score-hero-unlock-rec-${i}`}
                          >
                            <p className="flex items-start gap-2 text-[12.5px] font-semibold text-sky-100 leading-snug">
                              <span className="mt-0.5 shrink-0 h-4 w-4 rounded-full bg-sky-500/25 text-sky-200 text-[10px] font-mono-edge flex items-center justify-center">{i + 1}</span>
                              <span>{rec.title}</span>
                            </p>
                            <p className="mt-1 pl-6 text-[12px] text-slate-300 leading-relaxed">{rec.detail}</p>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <p className="mt-3 text-[12px] text-slate-400 leading-relaxed">{result.framework_note}</p>
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={onUnlock}
                      disabled={unlocking}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-sky-500 hover:bg-sky-400 disabled:opacity-70 text-white text-[14px] font-semibold px-5 h-11 transition-colors"
                      data-testid="free-score-hero-result-cta"
                    >
                      {unlocking ? <Loader2 size={15} className="animate-spin" /> : null}
                      Unlock Full Audit
                      {!unlocking && <ArrowRight size={15} />}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function ResultTile({ label, value, tone = 'muted', unit = '/100' }) {
  const tint = tone === 'accent' ? 'text-sky-300' : 'text-slate-200';
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
      <p className="text-[10px] font-mono-edge tracking-[0.22em] uppercase text-slate-400">{label}</p>
      <p className={`mt-1.5 text-3xl font-semibold tabular-nums ${tint}`}>
        {value}
        <span className="text-[13px] text-slate-500 font-normal ml-1">{unit}</span>
      </p>
    </div>
  );
}

function GapBlock({ label, gap, testid }) {
  // A quote alone is not actionable — only render when the LLM also
  // produced an issue or a fix; otherwise the block would look like
  // "we quoted you and said nothing about it".
  if (!gap || (!gap.issue && !gap.fix)) return null;
  return (
    <div
      className="mt-4 rounded-xl border border-amber-500/25 bg-amber-500/[0.04] p-3.5"
      data-testid={testid}
    >
      <p className="text-[10.5px] font-mono-edge tracking-[0.22em] uppercase text-amber-300">
        {label}
      </p>
      {gap.quote && (
        <blockquote className="mt-1.5 border-l-2 border-amber-400/60 pl-3 text-[12.5px] italic text-slate-200 leading-snug">
          &ldquo;{gap.quote}&rdquo;
        </blockquote>
      )}
      {gap.issue && (
        <p className="mt-2 text-[12.5px] text-slate-300 leading-relaxed">
          <span className="text-amber-200 font-semibold">Why it&rsquo;s weak: </span>
          {gap.issue}
        </p>
      )}
      {gap.fix && (
        <p className="mt-1.5 text-[12.5px] text-slate-300 leading-relaxed">
          <span className="text-sky-300 font-semibold">Fix: </span>
          {gap.fix}
        </p>
      )}
    </div>
  );
}

export default FreeScoreHero;
